package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.EventTypeMetadata
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.displayName
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/**
 * M10 Stage 4：lambda 形参相关诊断（E663 / E664）。
 *
 * 维度：
 * - **E663** lambda 形参类型推断失败：lambda 处于 `propName = { params -> ... }`
 *   但 propName 不在 [EventTypeMetadata.resolveLambdaParamTypes] 表中、且至少有一个
 *   形参未显式标注时报 INFO，提示用户显式标注。
 * - **E664** net.request callback 形参标注与推断不容：callback 是 [ExprAst.LambdaExpr]
 *   且首形参显式标注为非可空、非 Any 的具体类型时报 INFO（payload 实际是 Any?）。
 *
 * 设计要点：
 * - 仅 [ExprAst.LambdaExpr] 触发；[ExprAst.FunRefExpr] / 命名函数引用不在范围
 * - net.request 的 callback 通常单参 result，本 checker 只检查首形参
 * - 简化策略：标注是 Nullable 包装即放行——用户认可"可空 payload 兜底"的约定
 */
internal class LambdaParamChecker(
    private val diag: DiagnosticCollector,
    @Suppress("unused") private val env: VarTypeEnv,
) {

    /**
     * E663：处理 `propName = { params -> ... }` 形态的 lambda。
     *
     * 触发条件：
     * 1. lambda 至少有一个形参未标注（paramTypes 不与 params 等长 / 对应位置为 null）
     * 2. [EventTypeMetadata.resolveLambdaParamTypes] 不识别 propName
     *
     * 命中后取**第一个**未标注的形参名作为诊断位的形参参数；让 IDE 可定位到具体形参。
     */
    fun onLambdaInPropAssign(propName: String, lambda: ExprAst.LambdaExpr) {
        // 没有形参的 lambda（含隐式 it 已被 parser 规范化为 params=["it"]）也走这里：
        // 隐式 it 单参未标注，仍可触发 E663
        if (lambda.params.isEmpty()) return
        val firstUnannotated = firstUnannotatedParam(lambda) ?: return
        if (EventTypeMetadata.resolveLambdaParamTypes(propName) != null) return
        diag.info(DslError.E663_LAMBDA_PARAM_TYPE_INFERENCE_FAILED, lambda.position, firstUnannotated)
    }

    /**
     * E664：处理 [ExprAst.NetRequestCall.callback] 形态的 lambda。
     *
     * 触发条件：
     * 1. callback 是 [ExprAst.LambdaExpr]（FunRefExpr / null 不检查）
     * 2. paramTypes 首位非 null（用户显式标注了首形参类型）
     * 3. 解析后的类型既不是 [IrType.AnyT] 也不是 [IrType.Nullable]——这是简化策略：
     *    标注必须是可空类型才能正确接收 Any? payload；标注成具体非空类型一定不容
     */
    fun onNetRequestCallback(callback: ExprAst) {
        if (callback !is ExprAst.LambdaExpr) return
        if (callback.params.isEmpty()) return
        val firstAnnotation = callback.paramTypes.getOrNull(0) ?: return
        val resolved = TypeResolver.resolve(firstAnnotation) ?: return
        // 放行：Any 或任意 Nullable 包装——这两类标注能正确接收 Any? payload
        if (resolved == IrType.AnyT) return
        if (resolved is IrType.Nullable) return
        diag.info(
            DslError.E664_NET_REQUEST_CALLBACK_TYPE_MISMATCH,
            callback.position,
            callback.params[0],
            firstAnnotation.displayName(),
            "Any?",
        )
    }

    /**
     * 取第一个未标注的形参名；全部已标注（paramTypes 与 params 等长且无 null）时返回 null。
     *
     * paramTypes 可能为空（旧路径）—— 视为"全部未标注"，返回 params[0]。
     */
    private fun firstUnannotatedParam(lambda: ExprAst.LambdaExpr): String? {
        val types = lambda.paramTypes
        if (types.isEmpty()) return lambda.params.firstOrNull()
        for ((idx, name) in lambda.params.withIndex()) {
            val ann = types.getOrNull(idx)
            if (ann == null) return name
        }
        return null
    }
}
